import React, { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "react-feather";

const PAGE_SIZES = [25, 50, 100];

/**
 * Client-side paging for the Duty List list views (Summary, Employees, New
 * Assignments, Blank). A whole-branch month is several hundred to a few
 * thousand lines, all already loaded, so this only slices what is drawn -
 * print and Excel still take every filtered line. Back to page 1 whenever
 * the list itself changes (a new filter), as the Detail grid does.
 */
export const usePaged = (list, initialSize = 50) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(initialSize);
  useEffect(() => setPage(0), [list, size]);

  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(page, pageCount - 1);
  const start = current * size;
  const pageRows = useMemo(() => list.slice(start, start + size), [list, start, size]);

  return { pageRows, start, page: current, pageCount, size, total, setPage, setSize };
};

export const DutyListPager = ({ paged }) => {
  const { start, page, pageCount, size, total, setPage, setSize } = paged;
  if (total <= PAGE_SIZES[0]) return null;

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mt-2 small">
      <span className="text-muted">
        {start + 1}-{Math.min(total, start + size)} of {total}
      </span>
      <select className="form-select form-select-sm" style={{ width: "auto" }} value={size} onChange={(e) => setSize(Number(e.target.value))}>
        {PAGE_SIZES.map((s) => (
          <option key={s} value={s}>
            {s} per page
          </option>
        ))}
      </select>
      <div className="btn-group">
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(0)} title="First page">
          <ChevronsLeft size={14} />
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page === 0} onClick={() => setPage(page - 1)} title="Previous page">
          <ChevronLeft size={14} />
        </button>
        <span className="btn btn-sm btn-outline-secondary disabled text-dark">
          Page {page + 1} of {pageCount}
        </span>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)} title="Next page">
          <ChevronRight size={14} />
        </button>
        <button type="button" className="btn btn-sm btn-outline-secondary" disabled={page >= pageCount - 1} onClick={() => setPage(pageCount - 1)} title="Last page">
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};
